import { dniPlatne } from '../../utils/dniPlatne';

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const startOfDay = (d) => {
  if (!d) return null;
  const x = d instanceof Date ? d : new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
};

export const daysInclusive = (from, to) => {
  const s = startOfDay(from);
  const e = startOfDay(to);
  if (!s || !e) return 0;
  const diff = Math.round((e - s) / MS_PER_DAY);
  return diff >= 0 ? diff + 1 : 0;
};

export const getEquipmentDailyRate = (equipment, isLongTermRental) => {
  if (!equipment) return 0;
  const regular = Number(equipment.price_per_day ?? equipment.price ?? 0) || 0;
  const longTerm =
    Number(
      equipment.price_over_7 ??
      equipment.priceOver7 ??
      equipment.long_term_price ??
      0
    ) || 0;
  return isLongTermRental && longTerm > 0 ? longTerm : regular;
};

/**
 * Kwoty wierszy tabeli sprzętu w ofercie: `[{ daily, total }]` w kolejności `selectedEq`.
 *
 * Suma wierszy MUSI równać się kwocie oferty (test odbiorczy 11.10.2026): wiersze liczyły się
 * ze stawek bazowych, a „CENA” z wyceny serwera (POST /api/price-quote) z cennikiem i zestawami,
 * więc klient widział pozycje, które nie sumowały się do ceny. Przy świeżej wycenie wiersze idą
 * z niej (`items[].total` po `equipmentId`), zaokrąglone do grosza tak, że ostatni wiersz
 * domyka różnicę do `grandTotal`; bez wyceny ze stawek lokalnych, jak suma lokalna.
 */
export const offerRowAmounts = (selectedEq, quoteData, chargeableDays, isLongTermRental) => {
  const cents = (n) => Math.round((Number(n) || 0) * 100);
  const days = Number(chargeableDays) || 0;
  const byId = new Map(((quoteData && quoteData.items) || []).map((it) => [it.equipmentId, it]));
  const useQuote = !!quoteData && selectedEq.every((eq) => byId.has(eq.id));
  const totalsCents = selectedEq.map((eq) => (useQuote
    ? cents(byId.get(eq.id).total)
    : cents(getEquipmentDailyRate(eq, isLongTermRental) * days)));
  if (useQuote && totalsCents.length) {
    const diff = cents(quoteData.grandTotal) - totalsCents.reduce((a, b) => a + b, 0);
    totalsCents[totalsCents.length - 1] += diff;
  }
  return selectedEq.map((eq, i) => {
    const total = totalsCents[i] / 100;
    const daily = useQuote
      ? (days > 0 ? Math.round((total / days) * 100) / 100 : 0)
      : getEquipmentDailyRate(eq, isLongTermRental);
    return { daily, total };
  });
};

export const normalizeEmail = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

export const extractClientsPayload = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export const generateOfferNumber = (offerId, createdAt = new Date()) => {
  const date = createdAt instanceof Date ? createdAt : new Date(createdAt);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const paddedId = String(offerId || 0).padStart(6, '0');
  return `O/${year}/${month}/${paddedId}`;
};

export const formatLocalDate = (d) => {
  if (!d) return null;
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** `RRRR-MM-DD` → `DD.MM.RRRR` (daty z GET /api/sent-offers); inny zapis bez zmian. */
export const ymdToPl = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(ymd || '');
};

/**
 * Wysłana oferta po terminie ważności (`valid_until` z GET /api/sent-offers, ostatni dzień
 * ważności). Okno oferty mówi wtedy, że kopia ma pierwotną datę (test odbiorczy 11.10.2026).
 */
export const isOfferExpired = (offer, today = formatLocalDate(new Date())) =>
  !!(offer && offer.valid_until && today > String(offer.valid_until));

/**
 * Dni terminu oferty: wszystkie, płatne i czy to długi wynajem. Oferta z Historii podaje dni płatne
 * zapisane przy niej (`sent_offers.days`): liczyła je wycena z chwili wysyłki, więc obowiązują też
 * po zmianie tabeli dni płatnych firmy. Bez zapisu reguła domyślna, jak w ofertach sprzed N.3.
 */
export const offerTermDays = (dateFrom, dateTo, zapisanePlatne = null) => {
  const totalDays = daysInclusive(dateFrom, dateTo);
  const zapisane = Number(zapisanePlatne);
  const chargeableDays = zapisane > 0 ? zapisane : dniPlatne(totalDays, null);
  return { totalDays, chargeableDays, isLongTerm: totalDays > 7 };
};

const opisWiersza = (item) => ({
  equipmentId: item.id,
  rodzajSprzet: String(item.category || ''),
  kategoria: String(item.category || ''),
  klasa: String(item.class || ''),
  marka: String(item.brand || ''),
  rozmiar: String(item.size || ''),
  ilosc: '1',
});

/**
 * Wiersze tabeli sprzętu do PDF oferty (`equip` w POST /api/generate-offer i /api/offers).
 * JEDNA funkcja dla formularza i dla Historii (test odbiorczy 11.10.2026): z historii PDF
 * składał się z cen bazowych sztuk i nie sumował się do „CENA”. Kwoty jak w `offerRowAmounts`;
 * serwer i tak dopasowuje sumę do CENA (Login/backend/lib/offerRows.js) i zapisuje wiersze
 * przy ofercie, żeby kopia z Historii była tym samym dokumentem.
 */
export const buildOfferEquipRows = (selectedEq, quoteData, chargeableDays, isLongTermRental) => {
  const list = Array.isArray(selectedEq) ? selectedEq : [];
  const days = Number(chargeableDays) || 0;
  const amounts = offerRowAmounts(list, quoteData, days, isLongTermRental);
  const amountById = new Map(list.map((eq, i) => [eq.id, amounts[i]]));

  // Zestawy z wyceny (test odbiorczy 11.10.2026): sztuki zestawu dostawały w PDF równą część ceny
  // zestawu bez słowa o nim (kije za 50 zł/dzień przy cenniku 15 zł). Teraz zestaw to jeden wiersz
  // z nazwą i JEGO ceną, a sztuki idą pod nim bez własnej ceny, jak ramka zestawu w panelu.
  const quoted = new Set(((quoteData && quoteData.items) || []).map((it) => it.equipmentId));
  const useQuote = !!quoteData && list.every((eq) => quoted.has(eq.id));
  const listIds = new Set(list.map((eq) => eq.id));
  const bundleById = new Map();
  if (useQuote) {
    (quoteData.bundles || []).forEach((bundle) => {
      const ids = (bundle && bundle.equipmentIds) || [];
      if (ids.length && ids.every((id) => listIds.has(id) && !bundleById.has(id))) {
        ids.forEach((id) => bundleById.set(id, bundle));
      }
    });
  }

  const rows = [];
  const emitted = new Set();
  const cents = (n) => Math.round((Number(n) || 0) * 100);
  list.forEach((item) => {
    if (emitted.has(item.id)) return;
    const bundle = bundleById.get(item.id);
    if (!bundle) {
      emitted.add(item.id);
      const { daily, total } = amountById.get(item.id);
      rows.push({
        ...opisWiersza(item),
        cenaDzien: daily.toFixed(2),
        liczbaDni: String(days),
        cenaLaczna: total.toFixed(2),
      });
      return;
    }
    const members = list.filter((eq) => bundleById.get(eq.id) === bundle);
    members.forEach((eq) => emitted.add(eq.id));
    // Kwota zestawu = suma kwot jego sztuk z wyceny, więc suma wierszy zostaje równa „CENA”.
    const totalCents = members.reduce((s, eq) => s + cents(amountById.get(eq.id).total), 0);
    rows.push({
      typ: 'zestaw',
      nazwaZestawu: String(bundle.name || ''),
      ilosc: '1',
      cenaDzien: (days > 0 ? Math.round(totalCents / days) / 100 : 0).toFixed(2),
      liczbaDni: String(days),
      cenaLaczna: (totalCents / 100).toFixed(2),
      // N.2: stawka różniła się między dniami, więc cenaDzien to średnia (ramka zestawu w Historii).
      ...(bundle.rateVaries === true ? { stawkaSrednia: true } : {}),
    });
    members.forEach((eq) => rows.push({ typ: 'wZestawie', ...opisWiersza(eq), nazwaZestawu: String(bundle.name || '') }));
  });

  let lp = 0;
  return rows.map((row) => ({ lp: row.typ === 'wZestawie' ? '' : String(++lp), ...row }));
};

/**
 * Wiersze PDF oferty z Historii: zapisane przy ofercie (`equipment_rows`, ten sam dokument, który
 * dostał klient), a dla ofert sprzed ich zapisu złożone z wyceny jak w formularzu. Przy wycenie
 * innej niż zapisana cena (cennik zmienił się od wysyłki) serwer skaluje wiersze do CENA.
 */
/** Zapisany przy ofercie wiersz PDF z daną sztuką (z ceną), albo `null`. */
export const savedOfferRowFor = (offer, equipmentId) => {
  const rows = offer && Array.isArray(offer.equipment_rows) ? offer.equipment_rows : [];
  return rows.find((r) => r && r.cenaDzien != null && Number(r.equipmentId) === Number(equipmentId)) || null;
};

/**
 * Sprzęt oferty z Historii w grupach do okna szczegółów: zestawy z wierszy zapisanych przy ofercie
 * (`{ bundle: { name, dailyRate, isAverage }, items }`, jak ramka zestawu w formularzu), reszta po jednej
 * sztuce. Oferta bez zapisanych wierszy: każda sztuka osobno, jak dotąd.
 */
export const savedOfferGroups = (offer) => {
  const equipment = offer && Array.isArray(offer.equipment) ? offer.equipment : [];
  const rows = offer && Array.isArray(offer.equipment_rows) ? offer.equipment_rows : [];
  const bundleOf = new Map();
  let current = null;
  rows.forEach((row) => {
    if (row && row.typ === 'zestaw') {
      current = { name: String(row.nazwaZestawu || ''), dailyRate: Number(row.cenaDzien) || 0, isAverage: row.stawkaSrednia === true };
    } else if (row && row.typ === 'wZestawie' && current) {
      bundleOf.set(Number(row.equipmentId), current);
    } else {
      current = null;
    }
  });
  const emitted = new Set();
  const groups = [];
  equipment.forEach((eq) => {
    if (emitted.has(eq.id)) return;
    const bundle = bundleOf.get(Number(eq.id));
    if (!bundle) {
      emitted.add(eq.id);
      groups.push({ bundle: null, items: [eq] });
      return;
    }
    const items = equipment.filter((e) => bundleOf.get(Number(e.id)) === bundle);
    items.forEach((e) => emitted.add(e.id));
    groups.push({ bundle, items });
  });
  return groups;
};

/**
 * Lustro Login/backend/lib/offerRows.js#dopasujDoCeny (12.10.2026): suma wierszy z ceną równa
 * CENA co do grosza, brakujące grosze dla największych reszt. Serwer robi to z każdym dokumentem
 * oferty; okno oferty z Historii robi to samo, żeby przy wycenie innej niż zapisana cena (cennik
 * zmienił się od wysyłki) pokazać te same stawki co PDF. Zgodność z serwerem pilnuje test
 * `__tests__/oknoOfertyZHistorii.test.js`.
 */
export const dopasujDoCeny = (wiersze, cenaCalosc) => {
  const lista = Array.isArray(wiersze) ? wiersze : [];
  const kwotaGr = (v) => {
    if (v == null || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  };
  const zl = (gr) => (gr / 100).toFixed(2);
  const celGr = kwotaGr(cenaCalosc);
  if (celGr == null || celGr < 0) return lista;
  const platne = [];
  lista.forEach((w, i) => { if (w.typ !== 'wZestawie') platne.push(i); });
  if (!platne.length) return lista;
  const wagi = platne.map((i) => Math.max(0, kwotaGr(lista[i].cenaLaczna) || 0));
  if (platne.reduce((s, i) => s + (kwotaGr(lista[i].cenaLaczna) || 0), 0) === celGr) return lista;
  // Podział jak lib/wartoscRezerwacji.js#rozdzielGrosze.
  const suma = wagi.reduce((a, b) => a + b, 0);
  const dokladne = suma > 0 ? wagi.map((w) => (celGr * w) / suma) : wagi.map(() => celGr / wagi.length);
  const czesci = dokladne.map((x) => Math.floor(x));
  let reszta = celGr - czesci.reduce((a, b) => a + b, 0);
  const kolejnosc = dokladne
    .map((x, i) => ({ i, ulamek: x - Math.floor(x) }))
    .sort((a, b) => (b.ulamek - a.ulamek) || (a.i - b.i));
  for (let k = 0; reszta > 0 && k < kolejnosc.length; k += 1, reszta -= 1) czesci[kolejnosc[k].i] += 1;
  const wynik = lista.slice();
  platne.forEach((i, k) => {
    const w = lista[i];
    const dni = Number(w.liczbaDni) || 0;
    wynik[i] = { ...w, cenaLaczna: zl(czesci[k]), cenaDzien: dni > 0 ? zl(Math.round(czesci[k] / dni)) : w.cenaDzien };
  });
  return wynik;
};

/**
 * Wiersze zapisane bez numeru sztuki (stara karta przeglądarki sprzed zapisu `equipmentId`)
 * dostają go po opisie: typ, marka, rozmiar i klasa wiersza równe tym samym polom sztuki oferty
 * (12.10.2026). Bez tego okno oferty nie umiało przypisać wierszy do sprzętu i pokazywało stawki
 * z karty sprzętu, choć PDF drukował zapisane wiersze. Zwraca `null`, gdy któregoś wiersza nie da
 * się przypisać albo któraś sztuka zostaje bez wiersza (np. sprzęt zmieniono od wysyłki).
 */
export const przypiszSprzetWierszom = (wiersze, sprzet) => {
  const lista = Array.isArray(wiersze) ? wiersze : [];
  const sztuki = Array.isArray(sprzet) ? sprzet : [];
  const n = (v) => String(v == null ? '' : v).trim().toLowerCase();
  const kluczWiersza = (w) => [w.rodzajSprzet || w.kategoria, w.marka, w.rozmiar, w.klasa].map(n).join('|');
  const kluczSztuki = (e) => [e.category, e.brand, e.size, e.class].map(n).join('|');
  const zajete = new Set(lista.filter((w) => w && w.equipmentId != null).map((w) => Number(w.equipmentId)));
  let komplet = true;
  const wynik = lista.map((w) => {
    if (!w || w.typ === 'zestaw' || w.equipmentId != null) return w;
    const klucz = kluczWiersza(w);
    const sztuka = sztuki.find((e) => !zajete.has(Number(e.id)) && kluczSztuki(e) === klucz);
    if (!sztuka) { komplet = false; return w; }
    zajete.add(Number(sztuka.id));
    return { ...w, equipmentId: sztuka.id };
  });
  if (!komplet || sztuki.some((e) => !zajete.has(Number(e.id)))) return null;
  return wynik;
};

export const historyOfferRows = (offer, quoteData) => {
  if (!offer) return [];
  if (Array.isArray(offer.equipment_rows) && offer.equipment_rows.length) return offer.equipment_rows;
  const { chargeableDays, isLongTerm } = offerTermDays(offer.date_from, offer.date_to, offer.days);
  return buildOfferEquipRows(offer.equipment || [], quoteData, chargeableDays, isLongTerm);
};
